import { faro } from "@grafana/faro-web-sdk";
import { captureEvent } from "@vega-core/utils/posthog/captureEvent";

export const OnboardingInviteType = {
  LP_USER: "lp_user",
  GP_COINVEST: "gp_coinvest",
  GP_WORKBENCH: "gp_workbench",
} as const;

const portalType = (): string | undefined => import.meta.env.VITE_PORTAL_TYPE;

const toStatusCode = (error: unknown): number | undefined => {
  if (typeof error !== "object" || error === null) return undefined;

  const { response } = error as { response?: { status?: unknown } };

  return typeof response?.status === "number" ? response.status : undefined;
};

export const trackInviteCreateAttempted = ({
  inviteType,
  roleCount,
}: {
  inviteType: string;
  roleCount: number;
}): void => {
  captureEvent("onboarding_invite_create_attempted", {
    portal_type: portalType(),
    invite_type: inviteType,
    role_count: roleCount,
  });
};

export const trackInviteCreateFailed = ({
  inviteType,
  roleCount,
  error,
}: {
  inviteType: string;
  roleCount: number;
  error: unknown;
}): void => {
  captureEvent("onboarding_invite_create_failed", {
    portal_type: portalType(),
    invite_type: inviteType,
    role_count: roleCount,
    status_code: toStatusCode(error),
  });
  faro.api.pushError(error as Error, {
    type: "onboarding",
    context: {
      flow: "onboarding",
      step: "invite_create",
      invite_type: inviteType,
    },
  });
};

export const trackRegistrationStarted = ({
  invitedUserId,
}: {
  invitedUserId: string | undefined;
}): void => {
  captureEvent("onboarding_registration_started", {
    portal_type: portalType(),
    invited_user_id: invitedUserId,
  });
};

export const trackRegistrationCompleted = ({
  invitedUserId,
  countryCode,
}: {
  invitedUserId: string | undefined;
  countryCode: string | undefined;
}): void => {
  captureEvent("onboarding_registration_completed", {
    portal_type: portalType(),
    invited_user_id: invitedUserId,
    country_code: countryCode,
  });
};

export const trackEmailActivationViewed = (): void => {
  captureEvent("onboarding_email_activation_viewed", {
    portal_type: portalType(),
  });
};

export const trackFirstLoginSucceeded = ({
  userId,
}: {
  userId: string;
}): void => {
  captureEvent("onboarding_first_login_succeeded", {
    portal_type: portalType(),
    user_id: userId,
  });
};

const EXPECTED_AUTH_REJECTIONS = new Set([401, 403]);

export const trackLoginFailed = ({ error }: { error: unknown }): void => {
  const statusCode = toStatusCode(error);

  captureEvent("onboarding_login_failed", {
    portal_type: portalType(),
    status_code: statusCode,
  });

  if (statusCode !== undefined && EXPECTED_AUTH_REJECTIONS.has(statusCode)) {
    return;
  }

  faro.api.pushError(error as Error, {
    type: "onboarding",
    context: { flow: "onboarding", step: "login" },
  });
};
